import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Button,
  Spinner,
  Tooltip,
  Switch,
  Tabs,
  TabsHeader,
  Tab,
  Dialog,
  DialogHeader,
  DialogBody,
} from '@material-tailwind/react';
import {
  BanknotesIcon,
  ChartBarIcon,
  ClockIcon,
  ScaleIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  FunnelIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { UserAuth } from '../../context/AuthContext';
import { getEstAircraftSales, getEstSalesInsights } from '../../fb-functions';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const NUMBER_FMT = new Intl.NumberFormat('en-US');
const formatNum = (v) => (v != null && !isNaN(v) ? NUMBER_FMT.format(Math.round(v)) : '');
const formatCurrency = (v) =>
  v != null && !isNaN(v)
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v)
    : '';
const formatCurrencyM = (v) =>
  v != null && !isNaN(v) ? (v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : `$${Math.round(v / 1000)}K`) : '';
const formatRatio = (v) => (v != null && !isNaN(v) ? `${(v * 100).toFixed(1)}%` : '');
const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

const DEFAULT_RATES = { piston: 4.9, turbineLow: 3.9, turbineHigh: 2.9 };
const DEFAULT_THRESHOLD = 3000000;

const GROUP_COLORS = { piston: '#2563eb', turbine: '#d4a017', unknown: '#607d8b' };
const GROUP_LABELS = { piston: 'Piston', turbine: 'Turbine', unknown: 'Unknown' };

const LOOKBACK_OPTIONS = [
  { value: 0.5, label: 'Last 6 months' },
  { value: 1, label: 'Last year' },
  { value: 2, label: 'Last 2 years' },
  { value: 3, label: 'Last 3 years' },
  { value: 5, label: 'Last 5 years' },
  { value: null, label: 'All time' },
];
const lookbackLabel = (v) =>
  (LOOKBACK_OPTIONS.find((o) => o.value === v)?.label || `last ${v} years`).toLowerCase();

// Commission tier: rate is applied to (and tiered on) the estimated sale
// price, not the ask — that is what commission would actually be charged on.
function commissionRate(row, rates, threshold) {
  if (row.group === 'piston') return rates.piston;
  if (row.group === 'turbine') return row.estSaleUsd >= threshold ? rates.turbineHigh : rates.turbineLow;
  return null;
}

function StatCard({ title, value, sub, icon: Icon, color = 'blue' }) {
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    amber: 'bg-amber-500',
    purple: 'bg-purple-500',
  };
  return (
    <Card className="shadow-sm">
      <CardBody className="p-4">
        <div className="flex items-center gap-4">
          <div className={`${colorClasses[color]} p-3 rounded-lg`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
          <div>
            <Typography variant="small" color="blue-gray" className="font-normal opacity-70">
              {title}
            </Typography>
            <Typography variant="h4" color="blue-gray">
              {value}
            </Typography>
            {sub && (
              <Typography className="text-[11px] font-medium text-blue-gray-600">
                {sub}
              </Typography>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function RateInput({ label, value, onChange, suffix = '%' }) {
  return (
    <div className="flex flex-col">
      <label className="text-[11px] font-semibold text-blue-gray-700 uppercase tracking-wide mb-1">
        {label}
      </label>
      <div className="flex items-center gap-1">
        <input
          type="number"
          step={suffix === '%' ? 0.1 : 100000}
          min={0}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className="w-24 border border-blue-gray-100 rounded-md px-2 py-1.5 text-sm text-blue-gray-800 focus:outline-none focus:border-blue-500"
        />
        <span className="text-sm text-blue-gray-700">{suffix}</span>
      </div>
    </div>
  );
}

export default function EstSalesPage() {
  const { user } = UserAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [lookbackYears, setLookbackYears] = useState(3);
  const [rates, setRates] = useState({ ...DEFAULT_RATES });
  const [threshold, setThreshold] = useState(DEFAULT_THRESHOLD);
  const [compsFor, setCompsFor] = useState(null); // listing row whose comps dialog is open
  const [weightBySellThrough, setWeightBySellThrough] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState(null);

  const generateInsights = useCallback(async () => {
    setInsightsLoading(true);
    setInsightsError(null);
    const res = await getEstSalesInsights({ lookbackYears, rates, threshold, weightBySellThrough });
    if (res.success) {
      setInsights(res.data);
    } else {
      setInsightsError(res.error || 'Failed to generate insights');
    }
    setInsightsLoading(false);
  }, [lookbackYears, rates, threshold, weightBySellThrough]);

  const fetchData = useCallback(async (lookback) => {
    setLoading(true);
    setError(null);
    const res = await getEstAircraftSales({ lookbackYears: lookback });
    if (res.success) {
      setPayload(res.data);
    } else {
      setError(res.error || 'Failed to load report');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData(lookbackYears);
  }, [fetchData, lookbackYears]);

  // Rows with commission applied at the current tier rates. weight is the
  // per-group historical sell-through probability when the toggle is on;
  // totals/charts use the weighted values, the table shows raw per-aircraft.
  const rows = useMemo(() => {
    if (!payload) return [];
    const st = payload.stats?.sellThrough;
    const weightFor = (group) =>
      !weightBySellThrough ? 1 : st?.[group]?.rate ?? st?.overall?.rate ?? 1;
    return payload.listings.map((l) => {
      const included = !l.noAsk && l.group !== 'unknown' && l.estSaleUsd != null;
      const rate = included ? commissionRate(l, rates, threshold) : null;
      const commissionUsd = included && rate != null ? Math.round((l.estSaleUsd * rate) / 100) : null;
      const weight = weightFor(l.group);
      return {
        ...l,
        included,
        rate,
        commissionUsd,
        weight,
        weightedEstSaleUsd: included ? l.estSaleUsd * weight : null,
        weightedCommissionUsd: commissionUsd != null ? commissionUsd * weight : null,
        expectedDaysRemaining:
          l.domMedian != null ? Math.max(0, Math.round(l.domMedian - (l.daysActiveSoFar || 0))) : null,
      };
    });
  }, [payload, rates, threshold, weightBySellThrough]);

  const included = useMemo(() => rows.filter((r) => r.included), [rows]);
  const excludedCount = rows.length - included.length;

  const totals = useMemo(() => {
    const askTotal = included.reduce((s, r) => s + r.askUsd, 0);
    const grossEst = included.reduce((s, r) => s + (r.weightedEstSaleUsd || 0), 0);
    const commission = included.reduce((s, r) => s + (r.weightedCommissionUsd || 0), 0);
    return { askTotal, grossEst, commission };
  }, [included]);

  const groupBreakdown = useMemo(() => {
    return ['piston', 'turbine'].map((g) => {
      const grp = included.filter((r) => r.group === g);
      const gs = payload?.stats?.groupStats?.[g];
      return {
        group: g,
        count: grp.length,
        askTotal: grp.reduce((s, r) => s + r.askUsd, 0),
        grossEst: grp.reduce((s, r) => s + (r.weightedEstSaleUsd || 0), 0),
        commission: grp.reduce((s, r) => s + (r.weightedCommissionUsd || 0), 0),
        ratioMedian: gs?.ratioMedian ?? null,
        domMedian: gs?.domMedian ?? null,
        sellThroughRate: payload?.stats?.sellThrough?.[g]?.rate ?? null,
      };
    });
  }, [included, payload]);

  // Per-model DOM medians within each group, for the Median DOM tooltip.
  // n >= 5 matches the server's threshold for using model-level stats.
  const groupDomBreakdown = useMemo(() => {
    if (!payload?.comps) return {};
    const median = (arr) => {
      const s = [...arr].sort((a, b) => a - b);
      const n = s.length;
      return n % 2 === 1 ? s[(n - 1) / 2] : (s[n / 2 - 1] + s[n / 2]) / 2;
    };
    const byGroupModel = {};
    payload.comps.forEach((c) => {
      if (c.dom == null || !c.modelKey || (c.group !== 'piston' && c.group !== 'turbine')) return;
      if (!byGroupModel[c.group]) byGroupModel[c.group] = {};
      if (!byGroupModel[c.group][c.modelKey]) byGroupModel[c.group][c.modelKey] = [];
      byGroupModel[c.group][c.modelKey].push(c.dom);
    });
    const out = {};
    const compCounts = {}; // modelKey → DOM comp count, including n < 5
    Object.entries(byGroupModel).forEach(([g, models]) => {
      Object.entries(models).forEach(([model, doms]) => {
        compCounts[model] = doms.length;
      });
      out[g] = Object.entries(models)
        .map(([model, doms]) => ({ model, median: median(doms), n: doms.length }))
        .filter((m) => m.n >= 5)
        .sort((a, b) => b.n - a.n);
    });
    return { byGroup: out, compCounts };
  }, [payload]);

  // Listed models whose sale-time estimate falls back to the group median
  const groupDomFallbackModels = useMemo(() => {
    const out = {};
    rows.forEach((r) => {
      if (r.group !== 'piston' && r.group !== 'turbine') return;
      if (r.domSource === 'model' || r.domMedian == null) return;
      if (!out[r.group]) out[r.group] = {};
      const key = r.formattedModel || '(unknown model)';
      if (!out[r.group][key]) {
        out[r.group][key] = {
          model: key,
          listings: 0,
          compsN: (r.modelCompsKey && groupDomBreakdown.compCounts?.[r.modelCompsKey]) || 0,
        };
      }
      out[r.group][key].listings += 1;
    });
    return Object.fromEntries(
      Object.entries(out).map(([g, models]) => [
        g,
        Object.values(models).sort((a, b) => b.listings - a.listings),
      ])
    );
  }, [rows, groupDomBreakdown]);

  // Sell-through timeline: each listing "clears" after its type's median DOM,
  // less the time it has already been on the market (overdue → clears now).
  const timeline = useMemo(() => {
    const sellable = included
      .filter((r) => r.expectedDaysRemaining != null)
      .sort((a, b) => a.expectedDaysRemaining - b.expectedDaysRemaining);
    let cumGross = 0;
    let cumCommission = 0;
    const points = [{ days: 0, cumGross: 0, cumCommission: 0 }];
    sellable.forEach((r) => {
      cumGross += r.weightedEstSaleUsd || 0;
      cumCommission += r.weightedCommissionUsd || 0;
      points.push({ days: r.expectedDaysRemaining, cumGross, cumCommission });
    });
    const atDays = (d) => {
      let v = { cumGross: 0, cumCommission: 0 };
      points.forEach((p) => {
        if (p.days <= d) v = p;
      });
      return v;
    };
    return { points, sixMonths: atDays(182), threeMonths: atDays(91), twelveMonths: atDays(365) };
  }, [included]);

  const modelChartData = useMemo(() => {
    const byModel = {};
    included.forEach((r) => {
      const key = r.formattedModel || 'Unknown';
      if (!byModel[key]) byModel[key] = { model: key, commission: 0, group: r.group, count: 0 };
      byModel[key].commission += r.weightedCommissionUsd || 0;
      byModel[key].count += 1;
    });
    return Object.values(byModel)
      .sort((a, b) => b.commission - a.commission)
      .slice(0, 14);
  }, [included]);

  // ─── AgGrid ────────────────────────────────────────────────────────────────
  const columnDefs = useMemo(
    () => [
      { field: 'reg', headerName: 'Reg', width: 100, pinned: 'left' },
      { field: 'year', headerName: 'Year', width: 80 },
      {
        field: 'formattedModel',
        headerName: 'Model',
        width: 160,
        cellStyle: { color: '#1e40af', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' },
        tooltipValueGetter: () => 'Click to see the sold comps behind this estimate',
      },
      {
        field: 'group',
        headerName: 'Type',
        width: 100,
        valueFormatter: (p) => GROUP_LABELS[p.value] || p.value,
        cellStyle: (p) => ({ color: GROUP_COLORS[p.value] || '#455a64', fontWeight: 600 }),
      },
      { field: 'askUsd', headerName: 'Ask', width: 130, valueFormatter: (p) => formatCurrency(p.value) },
      {
        field: 'ratioUsed',
        headerName: 'Sale/Ask',
        width: 110,
        valueFormatter: (p) => formatRatio(p.value),
      },
      {
        field: 'ratioSource',
        headerName: 'Ratio Basis',
        width: 120,
        valueGetter: (p) => (p.data.ratioSource !== 'none' ? `${p.data.ratioSource} n=${p.data.ratioN}` : '—'),
      },
      {
        field: 'estSaleUsd',
        headerName: 'Est. Sale',
        width: 130,
        valueFormatter: (p) => formatCurrency(p.value),
        cellStyle: { fontWeight: 600 },
      },
      { field: 'rate', headerName: 'Rate %', width: 90, valueFormatter: (p) => (p.value != null ? `${p.value}%` : '—') },
      {
        field: 'commissionUsd',
        headerName: 'Est. Commission',
        width: 150,
        valueFormatter: (p) => formatCurrency(p.value),
        cellStyle: { fontWeight: 600, color: '#1e40af' },
      },
      { field: 'domMedian', headerName: 'Median DOM', width: 120, valueFormatter: (p) => formatNum(p.value) },
      { field: 'daysActiveSoFar', headerName: 'Days Listed', width: 110, valueFormatter: (p) => formatNum(p.value) },
      {
        field: 'expectedDaysRemaining',
        headerName: 'Est. Days Left',
        width: 120,
        valueFormatter: (p) => (p.value === 0 ? 'Overdue' : formatNum(p.value)),
        cellStyle: (p) => (p.value === 0 ? { color: '#b45309', fontWeight: 600 } : null),
      },
      { field: 'source', headerName: 'Source', width: 110 },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({ sortable: true, resizable: true, filter: true, suppressMovable: true }),
    []
  );

  const getRowStyle = useCallback(
    (params) => (!params.data.included ? { opacity: 0.5, fontStyle: 'italic' } : null),
    []
  );

  const onCellClicked = useCallback((event) => {
    if (event.colDef.field === 'formattedModel') setCompsFor(event.data);
  }, []);

  // Comps behind the clicked listing's DOM median, per its fallback basis
  const dialogComps = useMemo(() => {
    if (!compsFor || !payload?.comps) return [];
    let subset;
    if (compsFor.domSource === 'model' && compsFor.modelCompsKey) {
      subset = payload.comps.filter((c) => c.modelKey === compsFor.modelCompsKey);
    } else if (compsFor.domSource === 'group') {
      subset = payload.comps.filter((c) => c.group === compsFor.group);
    } else {
      subset = payload.comps;
    }
    return [...subset].sort((a, b) => (b.dtSold || '').localeCompare(a.dtSold || ''));
  }, [compsFor, payload]);

  const dialogColumnDefs = useMemo(
    () => [
      { field: 'reg', headerName: 'Reg', width: 100 },
      { field: 'year', headerName: 'Year', width: 80 },
      { field: 'model', headerName: 'Model', width: 150 },
      { field: 'dtSold', headerName: 'Sold', width: 120, valueFormatter: (p) => formatDate(p.value) },
      { field: 'origPriceUsd', headerName: 'Orig. Ask', width: 130, valueFormatter: (p) => formatCurrency(p.value) },
      { field: 'salePriceUsd', headerName: 'Sale Price', width: 130, valueFormatter: (p) => formatCurrency(p.value) },
      { field: 'ratio', headerName: 'Sale/Ask', width: 100, valueFormatter: (p) => formatRatio(p.value) },
      {
        field: 'dom',
        headerName: 'DOM',
        width: 90,
        valueFormatter: (p) => (p.value != null ? formatNum(p.value) : '—'),
        cellStyle: { fontWeight: 600 },
      },
    ],
    []
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Spinner className="h-10 w-10" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-8 mx-2">
        <Card className="border border-red-200">
          <CardBody>
            <Typography color="red" className="font-medium">
              {error}
            </Typography>
            <Typography className="text-sm text-blue-gray-700 mt-1">
              This report requires Est Sales access. If you believe you should have access, contact Alex.
            </Typography>
            <Button size="sm" className="mt-3" onClick={() => fetchData(lookbackYears)}>
              Retry
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  const stats = payload?.stats || {};

  return (
    <div className="mt-6 mb-8 mx-2 flex flex-col gap-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Typography variant="h4" color="blue-gray">
            Est Aircraft Sales
          </Typography>
          <Typography className="text-sm font-normal text-blue-gray-700 mt-1">
            If every aircraft currently listed sold at historical sale/ask discounts — {included.length} aircraft
            included{excludedCount > 0 ? `, ${excludedCount} excluded (no ask price or unclassified)` : ''}. Based on{' '}
            {formatNum(stats.compsUsed)} sold comps
            {payload?.lookbackYears ? ` from the ${lookbackLabel(payload.lookbackYears)}` : ' (all time)'}.
          </Typography>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={lookbackYears === null ? 'all' : lookbackYears}
            onChange={(e) => setLookbackYears(e.target.value === 'all' ? null : Number(e.target.value))}
            className="border border-blue-gray-100 rounded-md px-2 py-1.5 text-sm text-blue-gray-800 bg-white focus:outline-none focus:border-blue-500"
          >
            {LOOKBACK_OPTIONS.map((o) => (
              <option key={String(o.value)} value={o.value === null ? 'all' : o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <Button size="sm" variant="outlined" className="flex items-center gap-2" onClick={() => fetchData(lookbackYears)}>
            <ArrowPathIcon className="h-4 w-4" /> Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} className="w-full md:w-96">
        <TabsHeader>
          <Tab value="dashboard" onClick={() => setActiveTab('dashboard')}>
            Dashboard
          </Tab>
          <Tab value="insights" onClick={() => setActiveTab('insights')}>
            <span className="flex items-center gap-2">
              <SparklesIcon className="h-4 w-4" /> AI Insights
            </span>
          </Tab>
        </TabsHeader>
      </Tabs>

      {activeTab === 'dashboard' && (
      <>
      {/* Headline stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <StatCard
          title="Aircraft For Sale"
          value={formatNum(rows.length)}
          sub={`${formatNum(included.length)} in totals${excludedCount > 0 ? ` · ${excludedCount} excluded (no ask / unclassified)` : ''}`}
          icon={PaperAirplaneIcon}
          color="blue"
        />
        <StatCard
          title={weightBySellThrough ? 'Gross Est. Sales (weighted)' : 'Gross Est. Sales'}
          value={formatCurrencyM(totals.grossEst)}
          sub={`vs ${formatCurrencyM(totals.askTotal)} total ask${weightBySellThrough ? ' · weighted by sell-through' : ''}`}
          icon={BanknotesIcon}
          color="green"
        />
        <StatCard
          title={weightBySellThrough ? 'Est. Commission (weighted)' : 'Total Est. Commission'}
          value={formatCurrencyM(totals.commission)}
          sub={`at ${rates.piston}% / ${rates.turbineLow}% / ${rates.turbineHigh}% tiers`}
          icon={ChartBarIcon}
          color="blue"
        />
        <StatCard
          title="Clears Within 6 Months"
          value={formatCurrencyM(timeline.sixMonths.cumGross)}
          sub={`${formatCurrencyM(timeline.sixMonths.cumCommission)} commission at historical pace`}
          icon={ClockIcon}
          color="amber"
        />
        <StatCard
          title="Median Sale/Ask"
          value={formatRatio(stats.globalRatio)}
          sub={`median ${formatNum(stats.globalDom)} days on market`}
          icon={ScaleIcon}
          color="purple"
        />
        <StatCard
          title="Historical Sell-Through"
          value={stats.sellThrough?.overall?.rate != null ? formatRatio(stats.sellThrough.overall.rate) : '—'}
          sub={
            stats.sellThrough?.overall?.rate != null
              ? `piston ${formatRatio(stats.sellThrough.piston?.rate)} · turbine ${formatRatio(stats.sellThrough.turbine?.rate)} of ${formatNum(stats.sellThrough.overall.ended)} ended listings sold`
              : 'no ended listings in window'
          }
          icon={FunnelIcon}
          color="amber"
        />
      </div>

      {/* Commission parameters */}
      <Card className="shadow-sm">
        <CardBody className="p-4">
          <div className="flex items-end flex-wrap gap-6">
            <div>
              <Typography variant="h6" color="blue-gray">
                Commission Parameters
              </Typography>
              <Typography className="text-[11px] font-medium text-blue-gray-600 max-w-xs">
                Rates apply to the estimated sale price. Turbine tier switches at the threshold.
              </Typography>
            </div>
            <RateInput label="Piston" value={rates.piston} onChange={(v) => setRates((r) => ({ ...r, piston: v }))} />
            <RateInput
              label="Turbine < threshold"
              value={rates.turbineLow}
              onChange={(v) => setRates((r) => ({ ...r, turbineLow: v }))}
            />
            <RateInput
              label="Turbine ≥ threshold"
              value={rates.turbineHigh}
              onChange={(v) => setRates((r) => ({ ...r, turbineHigh: v }))}
            />
            <RateInput label="Threshold" value={threshold} onChange={setThreshold} suffix="USD" />
            <Button
              size="sm"
              variant="text"
              onClick={() => {
                setRates({ ...DEFAULT_RATES });
                setThreshold(DEFAULT_THRESHOLD);
              }}
            >
              Reset
            </Button>
            <div className="flex flex-col gap-1 border-l border-blue-gray-100 pl-6 w-80">
              <div className="flex items-center gap-2">
                <Switch
                  checked={weightBySellThrough}
                  onChange={(e) => setWeightBySellThrough(e.target.checked)}
                  label={
                    <Typography className="text-sm font-medium text-blue-gray-800 whitespace-nowrap">
                      Weight by historical sell-through
                    </Typography>
                  }
                />
                <Tooltip
                  className="max-w-xs bg-blue-gray-900 text-white text-xs p-3 z-50"
                  content={`On: totals, charts and timeline assume only ${formatRatio(stats.sellThrough?.piston?.rate)} of pistons and ${formatRatio(stats.sellThrough?.turbine?.rate)} of turbines sell, per the last ${formatNum(stats.sellThrough?.overall?.ended)} ended listings. Unrecorded sales count as unsold, so this is conservative. Off: assumes every aircraft sells — the original "if we sold everything" question.`}
                >
                  <InformationCircleIcon className="h-4 w-4 text-blue-gray-600 cursor-help shrink-0" />
                </Tooltip>
              </div>
              <Typography className="text-[11px] font-medium text-blue-gray-600 min-h-[17px]">
                {weightBySellThrough
                  ? `Totals weighted: piston ${formatRatio(stats.sellThrough?.piston?.rate)}, turbine ${formatRatio(stats.sellThrough?.turbine?.rate)}.`
                  : 'Assumes every listed aircraft sells.'}
              </Typography>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Piston vs turbine breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {groupBreakdown.map((g) => (
          <Card key={g.group} className="shadow-sm border-t-4" style={{ borderTopColor: GROUP_COLORS[g.group] }}>
            <CardBody className="p-4">
              <div className="flex justify-between items-center mb-2">
                <Typography variant="h6" color="blue-gray">
                  {GROUP_LABELS[g.group]}
                </Typography>
                <Typography className="text-sm font-semibold text-blue-gray-700">
                  {g.count} aircraft
                </Typography>
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <Typography className="text-[11px] font-semibold text-blue-gray-600 uppercase">Gross Est.</Typography>
                  <Typography className="text-base font-bold text-blue-gray-800">{formatCurrencyM(g.grossEst)}</Typography>
                </div>
                <div>
                  <Typography className="text-[11px] font-semibold text-blue-gray-600 uppercase">Commission</Typography>
                  <Typography className="text-base font-bold text-blue-gray-800">{formatCurrencyM(g.commission)}</Typography>
                </div>
                <div>
                  <Typography className="text-[11px] font-semibold text-blue-gray-600 uppercase">Median DOM</Typography>
                  <Tooltip
                    className="bg-blue-gray-900 text-white text-xs p-3 z-50"
                    placement="bottom"
                    content={
                      <div className="flex flex-col gap-1">
                        {groupDomBreakdown.byGroup?.[g.group]?.length > 0 ? (
                          <>
                            <span className="font-semibold mb-1">
                              Median days on market by model ({GROUP_LABELS[g.group].toLowerCase()} comps)
                            </span>
                            {groupDomBreakdown.byGroup[g.group].slice(0, 12).map((m) => (
                              <div key={m.model} className="flex justify-between gap-6">
                                <span>{m.model}</span>
                                <span className="font-semibold">
                                  {formatNum(m.median)}d <span className="font-normal opacity-80">(n={m.n})</span>
                                </span>
                              </div>
                            ))}
                            {groupDomBreakdown.byGroup[g.group].length > 12 && (
                              <span className="opacity-80 mt-1">
                                +{groupDomBreakdown.byGroup[g.group].length - 12} more models
                              </span>
                            )}
                          </>
                        ) : (
                          <span>No models with 5+ sold comps in this group.</span>
                        )}
                        {groupDomFallbackModels[g.group]?.length > 0 && (
                          <>
                            <span className="font-semibold mt-2 border-t border-white/20 pt-2">
                              Listed models using this group median ({formatNum(g.domMedian)}d — under 5 comps each)
                            </span>
                            {groupDomFallbackModels[g.group].slice(0, 12).map((m) => (
                              <div key={m.model} className="flex justify-between gap-6">
                                <span>
                                  {m.model}
                                  {m.listings > 1 ? ` ×${m.listings}` : ''}
                                </span>
                                <span className="opacity-80">
                                  {m.compsN > 0 ? `${m.compsN} comp${m.compsN === 1 ? '' : 's'}` : 'no comps'}
                                </span>
                              </div>
                            ))}
                            {groupDomFallbackModels[g.group].length > 12 && (
                              <span className="opacity-80 mt-1">
                                +{groupDomFallbackModels[g.group].length - 12} more models
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    }
                  >
                    <Typography className="text-base font-bold text-blue-gray-800 cursor-help w-fit border-b border-dotted border-blue-gray-300">
                      {g.domMedian != null ? `${formatNum(g.domMedian)}d` : '—'}
                    </Typography>
                  </Tooltip>
                </div>
                <div>
                  <Typography className="text-[11px] font-semibold text-blue-gray-600 uppercase">Sell-Through</Typography>
                  <Typography className="text-base font-bold text-blue-gray-800">
                    {g.sellThroughRate != null ? formatRatio(g.sellThroughRate) : '—'}
                  </Typography>
                </div>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      {/* Sell-through timeline */}
      <Card className="shadow-sm">
        <CardBody className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <Typography variant="h6" color="blue-gray">
              Sell-Through Timeline
            </Typography>
            <Tooltip
              className="max-w-sm bg-blue-gray-900 text-white text-xs p-3 z-50"
              content={
                <div className="flex flex-col gap-1.5">
                  <span className="font-semibold">How this chart works</span>
                  <span>
                    Each aircraft is assumed to sell once it reaches its historical median days-on-market
                    (per model when the comps sample allows, otherwise piston/turbine average).
                  </span>
                  <span>
                    Time already spent on the market counts: an aircraft&apos;s expected clear date is its
                    median DOM minus the days it has been listed so far. Aircraft already past their median
                    DOM are shown clearing immediately, at day 0 — which is why the curve starts with a jump.
                  </span>
                  <span>
                    Green shows cumulative estimated gross sales; blue shows cumulative estimated commission
                    at the current tier rates. Dashed lines mark 3, 6 and 12 months from today.
                  </span>
                  <span className="italic">
                    This is pacing at historical medians — a planning estimate, not a forecast of actual
                    closings.
                  </span>
                </div>
              }
            >
              <InformationCircleIcon className="h-5 w-5 text-blue-gray-600 cursor-help" />
            </Tooltip>
          </div>
          <Typography className="text-[11px] font-medium text-blue-gray-600 mb-3">
            Cumulative estimated revenue as inventory clears at each type&apos;s historical median days-on-market,
            net of time already listed. Aircraft past their median DOM count as clearing immediately.
          </Typography>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timeline.points}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eceff1" />
                <XAxis
                  dataKey="days"
                  type="number"
                  domain={[0, 400]}
                  ticks={[0, 91, 182, 274, 365]}
                  tickFormatter={(d) => `${Math.round(d / 30.4)}mo`}
                  tick={{ fontSize: 12, fill: '#455a64' }}
                />
                <YAxis tickFormatter={formatCurrencyM} tick={{ fontSize: 12, fill: '#455a64' }} width={70} />
                <RechartsTooltip
                  formatter={(v, name) => [formatCurrency(v), name === 'cumGross' ? 'Gross Sales' : 'Commission']}
                  labelFormatter={(d) => `${d} days`}
                />
                <Legend formatter={(v) => (v === 'cumGross' ? 'Cumulative Gross Sales' : 'Cumulative Commission')} />
                <ReferenceLine x={91} stroke="#90a4ae" strokeDasharray="4 4" label={{ value: '3mo', fontSize: 11, fill: '#455a64' }} />
                <ReferenceLine x={182} stroke="#90a4ae" strokeDasharray="4 4" label={{ value: '6mo', fontSize: 11, fill: '#455a64' }} />
                <ReferenceLine x={365} stroke="#90a4ae" strokeDasharray="4 4" label={{ value: '12mo', fontSize: 11, fill: '#455a64' }} />
                <Area type="stepAfter" dataKey="cumGross" stroke="#16a34a" fill="#16a34a" fillOpacity={0.12} strokeWidth={2} />
                <Area type="stepAfter" dataKey="cumCommission" stroke="#2563eb" fill="#2563eb" fillOpacity={0.12} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardBody>
      </Card>

      {/* Commission by model */}
      <Card className="shadow-sm">
        <CardBody className="p-4">
          <Typography variant="h6" color="blue-gray" className="mb-3">
            Est. Commission by Model
          </Typography>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={modelChartData} margin={{ bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eceff1" />
                <XAxis
                  dataKey="model"
                  angle={-35}
                  textAnchor="end"
                  interval={0}
                  tick={{ fontSize: 11, fill: '#455a64' }}
                />
                <YAxis tickFormatter={formatCurrencyM} tick={{ fontSize: 12, fill: '#455a64' }} width={70} />
                <RechartsTooltip
                  formatter={(v) => formatCurrency(v)}
                  labelFormatter={(m) => {
                    const d = modelChartData.find((x) => x.model === m);
                    return `${m} (${d?.count} aircraft)`;
                  }}
                />
                <Bar dataKey="commission" name="Est. Commission">
                  {modelChartData.map((d) => (
                    <Cell key={d.model} fill={GROUP_COLORS[d.group] || GROUP_COLORS.unknown} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardBody>
      </Card>

      {/* Listings table */}
      <Card className="shadow-sm">
        <CardBody className="p-4">
          <div className="flex justify-between items-center mb-3">
            <Typography variant="h6" color="blue-gray">
              Current Inventory ({rows.length})
            </Typography>
            <Typography className="text-[11px] font-medium text-blue-gray-600">
              Greyed rows are excluded from totals (no ask price or unclassified type). Ratio basis shows the
              comps sample used: model, group (piston/turbine), or global.
            </Typography>
          </div>
          <div className="ag-theme-quartz" style={{ height: 560 }}>
            <AgGridReact
              rowData={rows}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              getRowStyle={getRowStyle}
              onCellClicked={onCellClicked}
              tooltipShowDelay={300}
              domLayout="normal"
            />
          </div>
          <Typography className="text-[11px] font-medium text-blue-gray-600 mt-3">
            Comps pool: {formatNum(stats.compsUsed)} used, {formatNum(stats.compsExcluded)} excluded as ratio
            outliers{stats.unclassifiedComps > 0 ? `, ${formatNum(stats.unclassifiedComps)} unclassified` : ''}. Estimates
            only — every transaction is negotiable. Generated {payload?.generatedAt ? new Date(payload.generatedAt).toLocaleString() : ''}
            {user?.email ? ` for ${user.email}` : ''}.
          </Typography>
        </CardBody>
      </Card>
      </>
      )}

      {activeTab === 'insights' && (
        <Card className="shadow-sm">
          <CardBody className="p-6">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
              <div>
                <Typography variant="h5" color="blue-gray">
                  AI Insights
                </Typography>
                <Typography className="text-sm font-normal text-blue-gray-700 mt-1">
                  Executive briefing written from the live numbers using the settings currently on the
                  Dashboard tab ({lookbackLabel(lookbackYears) || 'all time'}
                  {weightBySellThrough ? ', weighted by sell-through' : ', assuming everything sells'}).
                </Typography>
              </div>
              <Button
                size="sm"
                className="flex items-center gap-2 bg-lma-dark"
                disabled={insightsLoading}
                onClick={generateInsights}
              >
                <SparklesIcon className="h-4 w-4" />
                {insights ? 'Regenerate' : 'Generate Insights'}
              </Button>
            </div>

            {insightsLoading && (
              <div className="flex items-center gap-3 py-12 justify-center">
                <Spinner className="h-6 w-6" />
                <Typography className="text-sm text-blue-gray-700">
                  Crunching the numbers and writing the briefing — usually 15–30 seconds…
                </Typography>
              </div>
            )}

            {!insightsLoading && insightsError && (
              <Typography color="red" className="text-sm font-medium py-4">
                {insightsError}
              </Typography>
            )}

            {!insightsLoading && !insightsError && !insights && (
              <Typography className="text-sm text-blue-gray-700 py-8 text-center">
                No briefing yet — click Generate Insights to write one from the current data.
              </Typography>
            )}

            {!insightsLoading && insights?.insights && (
              <div className="flex flex-col gap-6">
                <div>
                  <Typography variant="h6" color="blue-gray" className="mb-2">
                    Executive Summary
                  </Typography>
                  <Typography className="text-[15px] leading-relaxed text-blue-gray-800">
                    {insights.insights.executiveSummary}
                  </Typography>
                </div>

                {insights.insights.headlines?.length > 0 && (
                  <div>
                    <Typography variant="h6" color="blue-gray" className="mb-2">
                      Headlines
                    </Typography>
                    <div className="flex flex-col gap-2">
                      {insights.insights.headlines.map((h, i) => (
                        <div key={i} className="border-l-4 border-lma-gold pl-3 py-1">
                          <Typography className="text-sm font-semibold text-blue-gray-800">{h}</Typography>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {insights.insights.insights?.length > 0 && (
                  <div>
                    <Typography variant="h6" color="blue-gray" className="mb-2">
                      Key Insights
                    </Typography>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {insights.insights.insights.map((ins, i) => (
                        <Card key={i} className="border border-blue-gray-50 shadow-none">
                          <CardBody className="p-4">
                            <Typography className="text-sm font-bold text-blue-gray-800 mb-1">
                              {ins.title}
                            </Typography>
                            <Typography className="text-sm text-blue-gray-700">{ins.detail}</Typography>
                          </CardBody>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {insights.insights.caveats?.length > 0 && (
                  <div>
                    <Typography variant="h6" color="blue-gray" className="mb-2">
                      Caveats
                    </Typography>
                    <ul className="list-disc pl-5 flex flex-col gap-1">
                      {insights.insights.caveats.map((c, i) => (
                        <li key={i}>
                          <Typography className="text-sm text-blue-gray-700">{c}</Typography>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <Typography className="text-[11px] font-medium text-blue-gray-600">
                  Generated {insights.generatedAt ? new Date(insights.generatedAt).toLocaleString() : ''} from{' '}
                  {formatNum(insights.facts?.inventory?.listedAircraft)} listings and{' '}
                  {formatNum(insights.facts?.compsPool?.compsUsed)} comps. AI-written from computed figures —
                  verify before sharing externally.
                </Typography>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Comps drill-down dialog */}
      <Dialog open={compsFor !== null} handler={() => setCompsFor(null)} size="lg">
        <DialogHeader className="flex items-start justify-between">
          <div>
            <Typography variant="h5" color="blue-gray">
              {compsFor?.formattedModel} — sold comps behind the estimate
            </Typography>
            <Typography className="text-sm font-normal text-blue-gray-700 mt-1">
              {compsFor?.domSource === 'model'
                ? `Sale time uses this model's own comps: median ${formatNum(compsFor?.domMedian)} days on market (n=${compsFor?.domN}).`
                : compsFor?.domSource === 'group'
                ? `Too few comps for this exact model, so sale time uses the ${GROUP_LABELS[compsFor?.group]?.toLowerCase()} group: median ${formatNum(compsFor?.domMedian)} days (n=${compsFor?.domN}).`
                : `Sale time uses the full comps set: median ${formatNum(compsFor?.domMedian)} days (n=${compsFor?.domN}).`}
              {compsFor && compsFor.ratioSource !== compsFor.domSource
                ? ` Note: the sale/ask ratio uses the ${compsFor.ratioSource} sample (n=${compsFor.ratioN}).`
                : ''}
            </Typography>
          </div>
          <Button variant="text" size="sm" className="p-2" onClick={() => setCompsFor(null)}>
            <XMarkIcon className="h-5 w-5 text-blue-gray-700" />
          </Button>
        </DialogHeader>
        <DialogBody className="pt-0">
          <div className="ag-theme-quartz" style={{ height: 440 }}>
            <AgGridReact
              rowData={dialogComps}
              columnDefs={dialogColumnDefs}
              defaultColDef={defaultColDef}
              domLayout="normal"
            />
          </div>
          <Typography className="text-[11px] font-medium text-blue-gray-600 mt-2">
            {formatNum(dialogComps.length)} comps shown. Rows with a dash in DOM had no recorded listing
            duration — they count toward the sale/ask ratio but not the days-on-market median.
          </Typography>
        </DialogBody>
      </Dialog>
    </div>
  );
}
